export interface FileMetadataLike {
  fileName: string;
  objectURL: string;
  s3BucketFileName?: string;
}

export interface FileMetadataDiff<T> {
  kept: T[];
  removed: T[];
}

/**
 * Reconciles the MFE's current file list against what is already in state.
 *
 * Rebuilding every entry from scratch would drop `s3BucketFileName`, and because
 * `dispatchUploadToS3` only uploads files lacking that key, adding or removing one file
 * would silently re-upload every survivor under a new key and orphan the old objects.
 *
 * `removed` carries only entries that reached S3, since those are the only ones with an
 * object to delete.
 */
export function buildFileMetadata<T extends FileMetadataLike>(
  files: File[],
  existing: T[],
  createObjectURL: (file: File) => string,
  extras?: Partial<Omit<T, 'fileName' | 'objectURL'>>,
): FileMetadataDiff<T> {
  const kept = files.map((file) => {
    const previous = existing.find((entry) => entry.fileName === file.name);

    if (previous?.s3BucketFileName) {
      return { ...previous };
    }

    return { fileName: file.name, objectURL: createObjectURL(file), ...extras } as T;
  });

  const incomingNames = new Set(files.map((file) => file.name));
  const removed = existing.filter(
    (entry) => !incomingNames.has(entry.fileName) && !!entry.s3BucketFileName,
  );

  return { kept, removed };
}
